import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/apiHelpers';
import { Box, Flex, Text, Tooltip, Input, Image } from '@chakra-ui/react';
import AliasImage from '../../media/images/AliasImage.svg';
import {
  Table,
  TableHead,
  TableHeaderSortCell,
  TableSortDirection,
  TableBody,
  TableRow,
  TableCell,
  HeadingText,
  SelectMenu,
  showToast,
  Button,
} from '@spotoninc/nexus-react';

import '../../styles/reskinned-classification-dropdown.css';
import '../../styles/reskinned-integration-dropdown.css';

// Classification options - simplified to just Expense and Revenue
const classificationOptions = ['Expense', 'Revenue'];

const ManageAliasAndClassification = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: TableSortDirection.ascending,
  });
  const [editingItem, setEditingItem] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);
  const [menuPortalTarget, setMenuPortalTarget] = useState(null);

  // Handle sorting
  const handleSort = (key, newDirection) => {
    setSortConfig({ key, direction: newDirection });
  };

  // Sort and filter data
  const sortedAndFilteredData = React.useMemo(() => {
    let filteredData = [...data];

    // Apply sorting
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === TableSortDirection.ascending ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === TableSortDirection.ascending ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  }, [data, sortConfig]);

  // API function to update classification and/or alias
  const updateItemData = async (id, classification, alias) => {
    try {
      const body = new FormData();
      if (id !== undefined) {
        body.append('plParentId', id);
        body.append('classification', classification);
        body.append('alias', alias);

        const response = await fetchWithAuth('/api/set_plparent_settings/', {
          method: 'POST',
          body: body,
        });

        // Parse the JSON response
        const data = await response.json();

        // Check if the response indicates a session expiration
        if (data.code !== undefined) {
          window.open('/login', '_self');
          showToast({
            priority: 'error',
            content: 'Session expired!',
            autoClose: 5000,
          });
          throw new Error('Session expired');
        }
        return data;
      }
    } catch (error) {
      console.error('Error updating item data:', error);
      throw error;
    }
  };

  // Enhanced handleClassificationChange with API integration
  const handleClassificationChange = async (id, newClassification) => {
    try {
      // Find the current item to get previous value for logging and to get current alias
      const item = data.find(item => item.id === id);
      const prevClassification = item ? item.classification : 'unknown';
      const currentAlias = item ? item.alias : '--';

      // Skip API call if value hasn't changed
      if (prevClassification === newClassification) {
        return;
      }

      // Update local state immediately for responsive UI
      setData(prevData =>
        prevData.map(itemToUpdate =>
          itemToUpdate.id === id
            ? {
                ...itemToUpdate,
                classification: newClassification,
                isUpdating: true,
              }
            : itemToUpdate
        )
      );

      // Call the API to update the classification (without blocking UI)
      updateItemData(id, newClassification, currentAlias)
        .then(() => {
          // Success - update the state to remove loading
          setData(prevData =>
            prevData.map(i => (i.id === id ? { ...i, isUpdating: false } : i))
          );

          showToast({
            priority: 'success',
            content: 'Classification updated',
            autoClose: 5000,
          });
        })
        .catch(error => {
          // Error - revert the classification value
          setData(prevData =>
            prevData.map(i =>
              i.id === id
                ? {
                    ...i,
                    isUpdating: false,
                    classification: prevClassification,
                  }
                : i
            )
          );
          showToast({
            priority: 'error',
            content: 'Failed to update classification',
            autoClose: 5000,
          });
          console.error('Error updating classification:', error);
        });
    } catch (error) {
      showToast({
        priority: 'error',
        content: 'Failed to update classification',
        autoClose: 5000,
      });
      console.error('Error in classification update process:', error);
    }
  };

  // Handle edit alias
  const handleEditAlias = item => {
    setEditingItem(item);
  };

  // Handle save alias with API integration
  const handleSaveAlias = async (id, newAlias) => {
    if (!newAlias.trim()) {
      newAlias = '--';
    }

    try {
      // Find the current item to get current classification
      const item = data.find(item => item.id === id);
      const currentClassification = item ? item.classification : 'Expense'; // Default to Expense
      const prevAlias = item ? item.alias : '--';

      // Skip API call if value hasn't changed
      if (prevAlias === newAlias) {
        setEditingItem(null);
        return;
      }

      // Update local state immediately for responsive UI
      setData(prevData =>
        prevData.map(itemToUpdate =>
          itemToUpdate.id === id
            ? {
                ...itemToUpdate,
                alias: newAlias,
                isUpdating: true,
              }
            : itemToUpdate
        )
      );

      // Exit edit mode immediately for better UX
      setEditingItem(null);

      // Call the API to update the alias (without blocking UI)
      updateItemData(id, currentClassification, newAlias)
        .then(() => {
          // Success - update the state to remove loading
          setData(prevData =>
            prevData.map(i => (i.id === id ? { ...i, isUpdating: false } : i))
          );
          showToast({
            priority: 'success',
            content: 'Alias updated',
            autoClose: 5000,
          });
        })
        .catch(error => {
          // Error - revert the alias value
          setData(prevData =>
            prevData.map(i =>
              i.id === id ? { ...i, isUpdating: false, alias: prevAlias } : i
            )
          );
          showToast({
            priority: 'error',
            content: 'Failed to update alias',
            autoClose: 5000,
          });
          console.error('Error updating alias:', error);
        });
    } catch (error) {
      showToast({
        priority: 'error',
        content: 'Failed to update alias',
        autoClose: 5000,
      });
      console.error('Error in alias update process:', error);
    }
  };

  // Fetch integration IDs from the API
  const fetchIntegrations = async () => {
    try {
      const body = new FormData();
      body.append('type', 'integration_ids');

      const response = await fetchWithAuth('/api/fetch_budget_settings/', {
        method: 'POST',
        body: body,
      });

      // Parse the JSON response
      const data = await response.json();

      // Check if the response indicates a session expiration
      if (data.code !== undefined) {
        window.open('/login', '_self');
        showToast({
          priority: 'error',
          content: 'Session expired!',
          autoClose: 5000,
        });
        return [];
      }
      return data;
    } catch (error) {
      console.error('Error fetching integration IDs:', error);
      // We don't show toast here because the useEffect will handle it
      throw error; // Re-throw to let the caller handle it
    }
  };

  // Fetch data for a specific integration ID
  const fetchDataByIntegrationId = async integrationId => {
    try {
      setLoading(true);

      const body = new FormData();
      body.append('integration_id', integrationId);

      const response = await fetchWithAuth('/api/fetch_plparent_settings/', {
        method: 'POST',
        body: body,
      });

      // Parse the JSON response
      const data = await response.json();

      // Check if the response indicates a session expiration
      if (data.code !== undefined) {
        window.open('/login', '_self');
        showToast({
          priority: 'error',
          content: 'Session expired!',
          autoClose: 3000,
        });
        return [];
      }

      return data;
    } catch (error) {
      console.error(
        `Error fetching data for integration ID ${integrationId}:`,
        error
      );
      throw error; // Re-throw to let the caller handle it
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when selectedIntegrationId changes
  useEffect(() => {
    // Skip if no integration ID is selected
    if (!selectedIntegrationId) return;

    // Define the async function inside useEffect
    const loadDataForIntegration = async () => {
      try {
        setLoading(true);

        // Fetch data for the selected integration
        const fetchedData = await fetchDataByIntegrationId(
          selectedIntegrationId
        );

        // Process the data to match our component's expected format
        const processedData = fetchedData.map(item => {
          // Get the classification from the API or default to 'Expense'
          let classification = item.classification || 'Expense';

          // Ensure the classification is one of the valid options
          if (!classificationOptions.includes(classification)) {
            classification = 'Expense'; // Default to Expense if not a valid option
          }

          return {
            id: item.id,
            description: item.desc || 'No description',
            classification: classification,
            alias: item.alias === '-' || !item.alias ? '--' : item.alias, // Show '--' if alias is '-' or empty
          };
        });

        // Update the state with the fetched data
        setData(processedData);
      } catch (error) {
        console.error(
          `Error loading data for integration ID ${selectedIntegrationId}:`,
          error
        );

        // Set empty data on error
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    // Call the async function
    loadDataForIntegration();
  }, [selectedIntegrationId]);

  // Properly handle async operations in useEffect
  useEffect(() => {
    // Define the async function inside useEffect
    const loadIntegrations = async () => {
      try {
        setLoading(true);
        const rawIntegrations = await fetchIntegrations();
        // Transform the data: map "app_name" to "label" and "id" to "value"
        const transformedIntegrations = rawIntegrations.map(item => ({
          label: item.app_name,
          value: item.id,
        }));

        setIntegrations(transformedIntegrations);
      } catch (error) {
        console.error('Failed to load integrations:', error);
        showToast({
          priority: 'error',
          content: 'Failed to load integrations',
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    // Call the async function
    loadIntegrations();
  }, [showToast]);

  // Set the menu portal target to the document body when the component mounts
  useEffect(() => {
    setMenuPortalTarget(document.body);
  }, []);

  return (
    <Box
      ml={{ base: '16px', md: '40px' }}
      mr={{ base: '16px', md: '72px' }}
      py={10}
    >
      <Flex
        justify={{ base: 'flex-start', md: 'space-between' }}
        align={{ base: 'flex-start', md: 'center' }}
        mb={8}
        direction={{ base: 'column', md: 'row' }}
      >
        <HeadingText variant="heading2">Alias and Classification</HeadingText>
        {!!selectedIntegrationId && (
          <Flex gap={2} align="center" mt={{ base: 4, md: 0 }}>
            <SelectMenu
              className="reskinned-location-dropdown"
              value={selectedIntegrationId || ''}
              placeholder="Select Integration"
              options={integrations.map(integration => ({
                label: integration.label,
                value: integration.value,
              }))}
              onChange={(e, value) => {
                if (value) {
                  setSelectedIntegrationId(value);
                }
              }}
              style={{
                minWidth: '150px',
              }}
              menuPortalTarget={menuPortalTarget}
              menuPlacement="auto"
              menuPosition="fixed"
              isSearchable={false}
            />
          </Flex>
        )}
      </Flex>
      {integrations.length === 0 ||
      (!selectedIntegrationId && sortedAndFilteredData.length === 0) ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={{ base: 6, md: 10 }}
          px={4}
          minH="300px"
        >
          <Box mb={16} mt={8}>
            <Image src={AliasImage} alt="Alias" />
          </Box>
          {integrations.length === 0 ? (
            <>
              <HeadingText size="small" mb={2} textAlign="center">
                Add your first integration
              </HeadingText>
              <Text
                textAlign="center"
                color="gray.600"
                maxW="450px"
                fontSize={{ base: 'sm', md: 'md' }}
              >
                Connect your accounting system to unlock AI-driven financial
                insights.
              </Text>
              <Button />
            </>
          ) : (
            <>
              <HeadingText size="small" mb={2} textAlign="center">
                Select an Integration
              </HeadingText>
              <Text
                textAlign="center"
                color="gray.600"
                maxW="450px"
                fontSize={{ base: 'sm', md: 'md' }}
                mt={2}
                mb={6}
              >
                Configure Location Aliases for each integration to enable
                location-specific benchmarks, analysis, and AI summaries in
                Profit Assist.
              </Text>
              {!selectedIntegrationId && (
                <Flex gap={2} align="center" mt={{ base: 4, md: 0 }}>
                  <SelectMenu
                    className="reskinned-integration-dropdown"
                    value={selectedIntegrationId || ''}
                    placeholder="Select Integration"
                    options={integrations.map(integration => ({
                      label: integration.label,
                      value: integration.value,
                    }))}
                    style={{
                      minWidth: '150px',
                    }}
                    onChange={(e, value) => {
                      if (value) {
                        setSelectedIntegrationId(value);
                      }
                    }}
                    menuPortalTarget={menuPortalTarget}
                    menuPlacement="auto"
                    menuPosition="fixed"
                    isSearchable={false}
                    buttonProps={{
                      style: {
                        backgroundColor: '#2D71E8',
                        color: 'white',
                      },
                    }}
                  />
                </Flex>
              )}
            </>
          )}
        </Flex>
      ) : (
        // Table
        <Box overflow="hidden" bg="white">
          <Box overflowX="auto">
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow variant="filled">
                  <TableHeaderSortCell
                    onSort={direction => handleSort('description', direction)}
                    direction={
                      sortConfig.key === 'description'
                        ? sortConfig.direction
                        : undefined
                    }
                    width="33.33%"
                  >
                    Description
                  </TableHeaderSortCell>
                  <TableHeaderSortCell
                    onSort={direction =>
                      handleSort('classification', direction)
                    }
                    direction={
                      sortConfig.key === 'classification'
                        ? sortConfig.direction
                        : undefined
                    }
                    width="33.33%"
                  >
                    Classification
                  </TableHeaderSortCell>
                  <TableHeaderSortCell
                    onSort={direction => handleSort('alias', direction)}
                    direction={
                      sortConfig.key === 'alias'
                        ? sortConfig.direction
                        : undefined
                    }
                    width="33.33%"
                  >
                    Alias
                  </TableHeaderSortCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} textAlign="center">
                      <Text>Loading...</Text>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedAndFilteredData.map(item => {
                    return (
                      <TableRow key={item.id} variant="bottom-divider">
                        <TableCell sx={{ padding: '16px' }}>
                          <Text
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace={{ base: 'normal', md: 'nowrap' }}
                            display="-webkit-box"
                            sx={{
                              WebkitLineClamp: { base: 2, md: 1 },
                              WebkitBoxOrient: 'vertical',
                              maxHeight: { base: '3em', md: '1.5em' },
                              lineHeight: '1.5em',
                            }}
                          >
                            {item.description}
                          </Text>
                        </TableCell>
                        <TableCell
                          sx={{
                            position: 'static',
                            padding: '16px',
                          }}
                        >
                          <Flex justify="center" width="100%">
                            <SelectMenu
                              className="reskinned-classification-dropdown"
                              value={item.classification}
                              options={classificationOptions.map(option => ({
                                label: option,
                                value: option,
                              }))}
                              onChange={(_, value) => {
                                handleClassificationChange(item.id, value);
                              }}
                              menuPortalTarget={menuPortalTarget}
                              menuPlacement="auto"
                              menuPosition="fixed"
                              isSearchable={false}
                              isDisabled={item.isUpdating}
                            />
                          </Flex>
                        </TableCell>
                        <TableCell
                          sx={{
                            cursor:
                              editingItem && editingItem.id === item.id
                                ? 'default'
                                : 'pointer',
                            position: 'relative',
                            minWidth: '100px',
                            padding: '16px',
                          }}
                        >
                          {editingItem && editingItem.id === item.id ? (
                            <Flex>
                              <Input
                                minWidth="100px"
                                defaultValue={item.alias}
                                size="sm"
                                mr={2}
                                autoFocus
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    handleSaveAlias(item.id, e.target.value);
                                  }
                                }}
                                onBlur={e =>
                                  handleSaveAlias(item.id, e.target.value)
                                }
                                isDisabled={item.isUpdating}
                              />
                            </Flex>
                          ) : (
                            <Tooltip label="Edit alias" placement="top">
                              <Box
                                as="div"
                                cursor="pointer"
                                width="100%"
                                height="100%"
                                display="flex"
                                alignItems="center"
                                onClick={() =>
                                  !item.isUpdating && handleEditAlias(item)
                                }
                                opacity={item.isUpdating ? 0.5 : 1}
                                pointerEvents={
                                  item.isUpdating ? 'none' : 'auto'
                                }
                                borderRadius="md"
                                transition="all 0.2s"
                                p="2px"
                                _hover={{
                                  color: 'blue.500',
                                  backgroundColor: 'rgba(49, 130, 206, 0.05)',
                                }}
                              >
                                <Text
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  whiteSpace={{ base: 'normal', md: 'nowrap' }}
                                  display="-webkit-box"
                                  sx={{
                                    WebkitLineClamp: { base: 2, md: 1 },
                                    WebkitBoxOrient: 'vertical',
                                    maxHeight: { base: '3em', md: '1.5em' },
                                    lineHeight: '1.5em',
                                  }}
                                >
                                  {item.alias}
                                </Text>
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ManageAliasAndClassification;
